import { defineStore, PiniaPluginContext } from "pinia";
import { reactive } from "vue";
import {
  BookingItem,
  CartState,
  SaleCartItem,
  Address,
  DeliveryOption,
} from "../models/store";
import { RetailProduct } from "../models/retail-product.model";
import Axios from "axios";
import { debounce, debounceByKey } from "../utils/debounce";

export interface Customer {
  first_name: string;
  last_name: string;
  business_name: string;
  email: string;
  telephone: string;
  address: Address;
}

const deliveryAddressCache = new Map<string, number>();

// Tracks products with a pending or in-flight hire-price recalc so the loading
// counter increments only once per product across rapid successive triggers.
const pendingHireRecalcs = reactive(new Set<number>());

type CartStore = ReturnType<typeof useCartStore>;

const runHirePriceRecalc = debounceByKey<
  number,
  (store: CartStore, item: BookingItem) => void
>((store, item) => {
  void (async () => {
    try {
      if (store.items.some((i) => i.product.id === item.product.id)) {
        await store.calculateHirePrice(item);
      }
    } finally {
      pendingHireRecalcs.delete(item.product.id);
    }
  })();
}, 300);

const runDeliveryRecalc = debounce((store: CartStore) => {
  void (async () => {
    try {
      await store.getTransportCost();
    } finally {
      store.calculatingDeliveryPrice = false;
    }
  })();
}, 300);

export const useCartStore = defineStore("cart", {
  state: (): CartState => ({
    tenantDetails: null,
    items: [],
    saleCartItems: [],
    customer: {
      first_name: "",
      last_name: "",
      business_name: "",
      email: "",
      telephone: "",
      address: {
        address_1: "",
        address_2: "",
        city: "",
        post_code: "",
        country: "",
      },
    },
    deliveryAddress: {
      address_1: "",
      address_2: "",
      city: "",
      post_code: "",
      country: "",
    },
    useCustomerAddressForDelivery: false,
    deliveryRequired: false,
    collectionRequired: false,
    useDeliveryAddressForCollection: false,
    collectionAddress: {
      address_1: "",
      address_2: "",
      city: "",
      post_code: "",
      country: "",
    },
    availableDeliveryMethods: [],
    pickup: {
      name: "Pickup",
      price: 0,
      tax_rate: 0,
      addressCriteria: {},
    },
    countryOptions: [],
    loadingDeliveryOptions: false,
    deliveryOptionsError: null,
    internalDeliveryOption: null,
    fulfillmentType: null,
    calculatingDeliveryPrice: false,

    deliveryPriceError: null,
    userLoggedIn: false,
    legacyCheckout: false,
    requiresAccountApplicationForHire: false,
    accountApplicationSubmitted: false,
  }),

  getters: {
    cartCount(state): number {
      const items = state.items || [];
      const saleItems = state.saleCartItems || [];
      return [...items, ...saleItems]
        .map((item: { quantity: number }) => item.quantity)
        .reduce((prev, next) => prev + next, 0);
    },

    hireItemsCount(state): number {
      return state.items.length;
    },

    saleItemsCount(state): number {
      return state.saleCartItems.length;
    },

    totalSales(state): number {
      return state.saleCartItems
        .map(
          (item: { quantity: number; product: RetailProduct }) =>
            item.quantity * item.product.price
        )
        .reduce((prev, next) => prev + next, 0);
    },

    totalSalesIncTax(state): number {
      return state.saleCartItems
        .map(
          (item: { quantity: number; product: RetailProduct }) =>
            item.quantity * item.product.price_inc_tax
        )
        .reduce((prev, next) => prev + next, 0);
    },

    deliveryCost(state): number | null {
      if (this.hireMode) {
        return state.internalDeliveryOption?.price ?? null;
      }
      if (!this.deliveryMethod) {
        return null;
      }
      return this.deliveryMethod?.price ?? null;
    },

    deliveryOrCollectionRequired(state): boolean {
      if (this.hireMode) {
        return state.deliveryRequired || state.collectionRequired;
      }
      return state.fulfillmentType === "delivery";
    },

    deliveryLoading(state): boolean {
      if (this.hireMode) {
        return state.calculatingDeliveryPrice;
      }
      return state.calculatingDeliveryPrice || state.loadingDeliveryOptions;
    },

    hirePriceLoading(): boolean {
      return pendingHireRecalcs.size > 0;
    },

    totalsLoading(): boolean {
      return this.hirePriceLoading || this.deliveryLoading;
    },

    totalSalesTax(state): number | null {
      const saleItemsTaxInPennies = Math.round(
        state.saleCartItems
          .map(
            (item: { quantity: number; product: RetailProduct }) =>
              item.quantity * item.product.price * 100 * item.product.tax_rate
          )
          .reduce((prev, next) => prev + next, 0)
      );
      const deliveryTaxInPennies = Math.round(
        (this.deliveryMethod?.price ?? 0) *
        100 *
        (this.deliveryMethod?.tax_rate ?? 0)
      );
      return (saleItemsTaxInPennies + deliveryTaxInPennies) / 100;
    },

    totalSalesIncludingDeliveryAndTax(state): number | null {
      if (
        this.deliveryMethod?.price === null ||
        this.deliveryMethod?.price === undefined
      ) {
        return null;
      }
      return (
        Math.round(
          (state.saleCartItems
            .map(
              (item) =>
                item.quantity * item.product.price * (item.product.tax_rate + 1)
            )
            .reduce((prev, next) => prev + next, 0) +
            (this.deliveryMethod?.price ?? 0) *
            ((this.deliveryMethod?.tax_rate ?? 0) + 1)) *
          100
        ) / 100
      );
    },


    totalHires(state): number {
      return state.items.reduce((prev, next) => {
        return prev + (next.hire_price ?? (50 * next.quantity))
      }, 0);
    },

    totalSalesAndHires(state): number {
      return this.totalSales + this.totalHires;
    },

    totalSalesAndHiresIncludingDeliveryAndTax(state): number {
      const sales = this.totalSalesAndHires;
      const tax = this.totalSalesTax ?? 0;
      const delivery = this.deliveryCost ?? 0;
      return sales + tax + delivery;
    },

    customerValid(state): boolean {
      if (state.userLoggedIn) {
        return true;
      }
      if (!state.customer) {
        return false;
      }
      return !!(
        state.customer.first_name &&
        state.customer.last_name &&
        state.customer.email &&
        state.customer.telephone &&
        state.customer.address.address_1 &&
        state.customer.address.city &&
        state.customer.address.post_code &&
        state.customer.address.country
      );
    },

    deliveryValid(state): boolean {
      if (!this.deliveryMethod) {
        return false;
      }
      if (this.deliveryMethod?.name === "Pickup") {
        return true;
      }
      if (state.deliveryRequired && !this.deliveryAddressValid) {
        return false;
      }
      if (state.collectionRequired && !this.collectionAddressValid) {
        return false;
      }
      return true;
    },

    hireSubmissionBlocked(state): boolean {
      return (
        state.requiresAccountApplicationForHire &&
        !state.accountApplicationSubmitted &&
        state.items.length > 0
      );
    },

    canSubmitCombined(state): boolean {
      return (
        this.customerValid &&
        this.deliveryValid &&
        this.totalSalesIncludingDeliveryAndTax !== null &&
        !this.hireSubmissionBlocked
      );
    },

    hasHireItems(state): boolean {
      return state.items.length > 0;
    },

    hasSaleItems(state): boolean {
      return state.saleCartItems.length > 0;
    },

    billingAddressValid(state): boolean {
      if (state.userLoggedIn) {
        return true;
      }
      return !!(
        state.customer.address.address_1 &&
        state.customer.address.city &&
        state.customer.address.post_code &&
        state.customer.address.country
      );
    },

    deliveryAddressValid(state): boolean {
      if (state.useCustomerAddressForDelivery) {
        return this.billingAddressValid;
      }
      return !!(
        state.deliveryAddress.address_1 &&
        state.deliveryAddress.city &&
        state.deliveryAddress.post_code &&
        state.deliveryAddress.country
      );
    },

    collectionAddressValid(state): boolean {
      if (state.useDeliveryAddressForCollection) {
        return this.deliveryAddressValid;
      }
      return !!(
        state.collectionAddress.address_1 &&
        state.collectionAddress.city &&
        state.collectionAddress.post_code &&
        state.collectionAddress.country
      );
    },

    isCalculatingDeliveryPrice(state): boolean {
      return state.calculatingDeliveryPrice;
    },

    externalDeliveryAvailable(state): boolean {
      // only show external delivery available if there are available delivery methods, no hire items and at least one sale item
      return (
        state.availableDeliveryMethods.length > 0 &&
        state.items.length === 0 &&
        state.saleCartItems.length > 0
      );
    },

    hireMode(state): boolean {
      // if the transaction has any hire items, then this is in hire mode only - unless it is legacy checkout
      return !this.legacyCheckout && this.hasHireItems;
    },

    deliveryMethod(state): DeliveryOption | null {
      let foundDeliveryMethod: DeliveryOption | null = null;
      if (this.hireMode) {
        if (state.deliveryRequired || state.collectionRequired) {
          return state.internalDeliveryOption ?? null;
        }
        return state.pickup;
      }
      if (state.fulfillmentType === "pickup") {
        return state.pickup;
      }
      if (state.availableDeliveryMethods.length > 0) {
        foundDeliveryMethod =
          state.availableDeliveryMethods.find((method) => {
            const addressCriteriaKeys = Object.keys(method.addressCriteria);
            if (addressCriteriaKeys.length === 0) {
              return true;
            }
            return addressCriteriaKeys.every((key) => {
              return (
                method.addressCriteria[
                key as keyof typeof method.addressCriteria
                ] ===
                this.deliveryAddress[key as keyof typeof method.addressCriteria]
              );
            });
          }) ?? null;
      }
      if (!foundDeliveryMethod) {
        foundDeliveryMethod = state.internalDeliveryOption ?? null;
      }
      return foundDeliveryMethod;
    },

    getDeliveryAddress(state): Address {
      if (state.useCustomerAddressForDelivery) {
        return state.customer.address;
      }
      return state.deliveryAddress;
    },

    getCollectionAddress(state): Address {
      if (state.useDeliveryAddressForCollection) {
        return this.getDeliveryAddress;
      }
      return state.collectionAddress;
    },

    submitPayload(state): {
      customer: Customer;
      delivery_address: Address;
      delivery_method: DeliveryOption | null;
      collection_address: Address;
      fulfillment_type: string | null;
      sale_items: {
        id: number;
        price: number;
        tax_rate: number;
        quantity: number;
      }[];
      hire_items: {
        id: number;
        quantity: number;
        on_hire: string | null;
        off_hire: string | null;
        deposit_payment: boolean;
        full_payment: boolean;
        amount: number;
      }[];
      total: number;
      total_tax: number;
      total_delivery: number;
      total_hire: number;
      total_sales: number;
      delivery_required: boolean;
      collection_required: boolean;
      is_internal_delivery: boolean;
    } {
      return {
        customer: state.customer,
        delivery_address: this.getDeliveryAddress,
        delivery_method: this.deliveryMethod,
        collection_address: this.getCollectionAddress,
        fulfillment_type: this.fulfillmentType,
        sale_items: state.saleCartItems.map(
          ({
            product,
            quantity,
          }: {
            product: RetailProduct;
            quantity: number;
          }) => ({
            id: product.id,
            price: product.price,
            tax_rate: product.tax_rate,
            quantity,
          })
        ),
        hire_items: state.items.map((item) => ({
          id: item.product.id,
          quantity: item.quantity,
          // need to include on / off hire dates
          on_hire: item.on_hire ? item.on_hire.toISOString() : null,
          off_hire: item.off_hire ? item.off_hire.toISOString() : null,
          deposit_payment: Boolean(item.hire_price),
          full_payment: Boolean(item.hire_price && item.hire_price > 0),
          amount: item.hire_price ?? 50 * item.quantity,
        })),
        total: this.totalSalesAndHiresIncludingDeliveryAndTax ?? 0,
        total_tax: this.totalSalesTax ?? 0,
        total_delivery: this.deliveryCost ?? 0,
        total_hire: this.totalHires,
        total_sales: this.totalSales,
        delivery_required: this.deliveryRequired,
        collection_required: this.collectionRequired,
        is_internal_delivery:
          this.deliveryMethod === this.internalDeliveryOption,
      };
    },

    actualDeliveryAddress(state): Address {
      if (state.useCustomerAddressForDelivery) {
        return state.customer.address;
      }
      return state.deliveryAddress;
    },

    actualCollectionAddress(state): Address {
      if (state.useDeliveryAddressForCollection) {
        return this.actualDeliveryAddress;
      }
      return state.collectionAddress;
    },

    billingAddress(state): Address {
      return state.customer.address;
    },
  },

  actions: {
    setLegacyCheckout(legacyCheckout: boolean) {
      this.legacyCheckout = legacyCheckout;
    },

    setUserLoggedIn(loggedIn: boolean) {
      this.userLoggedIn = loggedIn;
    },

    setAccountApplicationGate(requiresApplication: boolean, applicationSubmitted: boolean) {
      this.requiresAccountApplicationForHire = requiresApplication;
      this.accountApplicationSubmitted = applicationSubmitted;
    },

    setItems(items: BookingItem[]) {
      this.items = items;
    },

    addToBooking(item: BookingItem) {
      const existing = this.items.find(
        (i) => i.product.id === item.product.id
      );

      if (existing) {
        const replacementItem: BookingItem = {
          ...existing,
          quantity: existing.quantity + item.quantity,
          on_hire: item.on_hire ?? existing.on_hire,
          off_hire: item.off_hire ?? existing.off_hire,
        };
        this.items.splice(this.items.indexOf(existing), 1, replacementItem);
        this.scheduleHirePriceRecalc(replacementItem);
      } else {
        this.items.push(item);
        this.scheduleHirePriceRecalc(item);
      }
    },

    async calculateHirePrice(item: BookingItem) {
      if (!item.on_hire || !item.off_hire) {
        return;
      }
      let hirePrice: number | undefined;
      let priceError = false;
      try {
        const result = await Axios.post<{
          items: BookingItem[];
          prices: { price: number; error: boolean }[];
        }>("/api/v1/calculate-price", {
          items: [
            {
              product_id: item.product.id,
              on_hire: item.on_hire.toISOString(),
              off_hire: item.off_hire.toISOString(),
              quantity: item.quantity,
            },
          ],
        });
        hirePrice = result.data.prices[0]?.price ?? undefined;
        priceError = result.data.prices[0]?.error ?? false;
      } catch (error: any) {
        priceError = true;
      }
      const idx = this.items.findIndex(
        (i) => i.product.id === item.product.id
      );
      if (idx === -1) {
        return;
      }
      this.items.splice(idx, 1, {
        ...this.items[idx],
        hire_price: hirePrice,
        price_error: priceError,
      });
    },

    scheduleHirePriceRecalc(item: BookingItem) {
      const productId = item.product.id;
      pendingHireRecalcs.add(productId);
      runHirePriceRecalc(productId, this as CartStore, item);
    },

    addToSalesCart(item: SaleCartItem) {
      const inCart = this.saleCartItems.filter(
        (existing) => existing.product.id === item.product.id
      );
      if (inCart.length === 1) {
        // Add null check before spreading
        if (!inCart[0]) {
          this.saleCartItems.push(item);
          return;
        }
        const replacementItem = { ...inCart[0] };
        replacementItem.quantity += item.quantity;
        this.saleCartItems.splice(
          this.saleCartItems.indexOf(inCart[0]),
          1,
          replacementItem
        );
      } else {
        this.saleCartItems.push(item);
      }
    },

    increment(item: BookingItem | SaleCartItem) {
      item.quantity++;
      if (this.items.includes(item as BookingItem)) {
        this.scheduleHirePriceRecalc(item as BookingItem);
      }
    },

    decrement({ item, saleItem }: { item: BookingItem; saleItem: boolean }) {
      item.quantity--;
      if (item.quantity === 0 && !saleItem) {
        this.items.splice(this.items.indexOf(item), 1);
        return;
      }
      if (item.quantity === 0 && saleItem) {
        this.saleCartItems.splice(
          this.saleCartItems.indexOf(item as unknown as SaleCartItem),
          1
        );
        return;
      }
      if (!saleItem) {
        this.scheduleHirePriceRecalc(item);
      }
    },

    remove({ item, saleItem }: { item: BookingItem; saleItem: boolean }) {
      if (saleItem) {
        this.saleCartItems.splice(
          this.saleCartItems.indexOf(item as unknown as SaleCartItem),
          1
        );
        return;
      }
      this.items.splice(this.items.indexOf(item), 1);
    },

    updateQuantity({
      item,
      quantity,
      saleItem,
    }: {
      item: BookingItem | SaleCartItem;
      quantity: number;
      saleItem: boolean;
    }) {
      if (saleItem) {
        // Add null check before spreading
        if (!item) {
          return;
        }
        const replacementItem = { ...item } as SaleCartItem;
        replacementItem.quantity = quantity;
        this.saleCartItems.splice(
          this.saleCartItems.indexOf(item as SaleCartItem),
          1,
          replacementItem
        );
        return;
      }
      // Add null check before spreading
      if (!item) {
        return;
      }
      const replacementItem = { ...item } as BookingItem;
      replacementItem.quantity = quantity;
      this.items.splice(
        this.items.indexOf(item as BookingItem),
        1,
        replacementItem
      );
      this.scheduleHirePriceRecalc(replacementItem);
    },

    async determineDeliveryOptions() {
      await this.getTenantDetails();
      await this.getInternalDeliveryOption();
      await this.determineExternalDeliveryCollectionOptions();
    },

    async determineExternalDeliveryCollectionOptions() {
      // external delivery is only available if there are sale items and no hire items

      this.loadingDeliveryOptions = true;
      try {
        const result = await Axios.post<{
          delivery: DeliveryOption[];
          collection: DeliveryOption[];
        }>("/api/checkout/delivery-options", {
          address: this.deliveryAddress,
          product_ids: this.saleCartItems.map((item) => item.product.id),
        });
        const { delivery, collection } = result.data;
        this.availableDeliveryMethods = delivery;
        if (collection.length) {
          this.pickup = collection[0];
        }
        this.countryOptions = delivery
          .map((method: DeliveryOption) => method.addressCriteria?.country)
          .filter(Boolean) as string[];
        this.loadingDeliveryOptions = false;
        this.deliveryOptionsError = null;
      } catch (error: any) {
        this.loadingDeliveryOptions = false;
        this.deliveryOptionsError =
          "Unable to determine delivery options for this order, please contact us for assistance.";
      }
    },

    async getInternalDeliveryOption() {
      if (this.deliveryRequired) {
        if (this.useCustomerAddressForDelivery && !this.billingAddressValid) {
          this.internalDeliveryOption = null;
          return null;
        }
        if (!this.useCustomerAddressForDelivery && !this.deliveryAddressValid) {
          this.internalDeliveryOption = null;
          return null;
        }
      }

      if (this.collectionRequired && !this.collectionAddressValid) {
        this.internalDeliveryOption = null;
        return null;
      }

      this.calculatingDeliveryPrice = true;
      runDeliveryRecalc(this as CartStore);

      return null;
    },

    async getTransportCost() {
      if (!this.deliveryRequired && !this.collectionRequired) {
        this.internalDeliveryOption = null;
        return;
      }

      // check for hire items - if none present set collectionRequired to false
      // it may persist to true from a previous hire request session
      if (this.items.length === 0) {
        this.collectionRequired = false;
      }

      const formatAddr = (a: Address) =>
        `${a.address_1}, ${a.address_2 ?? ''}, ${a.city}, ${a.post_code}, ${a.country}`;

      const deliveryAddr = this.deliveryRequired ? this.actualDeliveryAddress : null;
      const collectionAddr = this.collectionRequired ? this.actualCollectionAddress : null;

      let deliveryPrice: number | null = null;
      let collectionPrice: number | null = null;

      try {
        if (deliveryAddr) {
          deliveryPrice = await this.getDeliveryOrCollectionPrice(
            formatAddr(deliveryAddr),
            deliveryAddr.googlePlaceId ?? null
          );
          if (!deliveryPrice) {
            this.internalDeliveryOption = null;
            throw "Delivery address is not valid";
          }
        }

        if (collectionAddr) {
          // Reuse delivery price when collection address matches (avoids a duplicate
          // in-flight request that would race the cache).
          if (deliveryAddr && formatAddr(collectionAddr) === formatAddr(deliveryAddr)) {
            collectionPrice = deliveryPrice;
          } else {
            collectionPrice = await this.getDeliveryOrCollectionPrice(
              formatAddr(collectionAddr),
              collectionAddr.googlePlaceId ?? null
            );
            if (!collectionPrice) {
              this.internalDeliveryOption = null;
              return;
            }
          }
        }
      } catch (error: any) {
        this.deliveryPriceError =
          "Unable to determine delivery/collection price for this order, please try a different address or contact us for assistance.";
        this.internalDeliveryOption = null;
        return;
      }

      this.deliveryPriceError = null;

      const tenantName = this.tenantDetails?.name ?? "";
      let name: string;
      if (this.deliveryRequired && this.collectionRequired) {
        name = `${tenantName} Delivery & Collection`;
      } else if (this.deliveryRequired) {
        name = `${tenantName} Delivery`;
      } else {
        name = `${tenantName} Collection`;
      }

      this.internalDeliveryOption = {
        name,
        price: (deliveryPrice ?? 0) + (collectionPrice ?? 0),
        tax_rate: this.tenantDetails?.defaultTaxRate ?? 0,
        addressCriteria: {},
      };
    },

    async getDeliveryOrCollectionPrice(
      address: string,
      placeId: string | null
    ) {
      const cacheKey = `${address}-${placeId}`;
      if (deliveryAddressCache.has(cacheKey)) {
        return deliveryAddressCache.get(cacheKey) as number | null;
      }
      const params = {
        address,
        place_id: placeId,
        factorToUse: this.getFactorToUse(),
      };
      const result = await Axios.get<{
        price: number;
      }>("/api/v1/delivery-price", { params });

      if (result.data.price) {
        deliveryAddressCache.set(cacheKey, result.data.price);
        return result.data.price ?? null;
      }
      deliveryAddressCache.delete(cacheKey);
      return null;
    },

    async getTenantDetails() {
      const result = await Axios.get<{
        name: string;
        defaultTaxRate: number;
        locations: { id: number; name: string }[];
      }>("/api/v1/shopfront-details");
      this.tenantDetails = result.data;
    },

    getFactorToUse(): 'sm' | 'md' | 'lg' {
      // if any of the items has a truck icon, use lg
      if (this.items.some(item => item.product.shopfront_extras?.icons?.some(icon => icon.name === 'truck'))) {
        return 'lg'
      }
      // if all of the items have car icons, use sm
      if (this.items.every(item => item.product.shopfront_extras?.icons?.every(icon => icon.name === 'car'))) {
        return 'sm';
      }
      // otherwise use md
      return 'md';
    },

    setDeliveryAddress(address: Address) {
      this.deliveryAddress = address;
    },

    setCustomer(customer: Customer) {
      this.customer = customer;
    },

    verifyRetailProductItem(item: RetailProduct) {
      const { id, price, tax_rate } = item;
      const existing = this.saleCartItems.find((i) => i.product.id === id);
      if (existing) {
        if (existing.product.price !== price) {
          existing.product.price = price;
        }
        if (existing.product.tax_rate !== tax_rate) {
          existing.product.tax_rate = tax_rate;
        }
      }
    },

    emptyCart(
      includeHireItems: boolean = false,
      includeSaleItems: boolean = false
    ) {
      if (includeHireItems) {
        this.items = [];
      }
      if (includeSaleItems) {
        this.saleCartItems = [];
      }
    },

    async verifyCart() {
      const productIds = this.saleCartItems.map(
        (item: SaleCartItem) => item.product.id
      );
      const products: RetailProduct[] = await fetch(
        `/api/checkout/verify-products?ids=${productIds.join(",")}`
      ).then((res) => res.json());

      products.forEach((product) => {
        this.verifyRetailProductItem(product);
      });
    },

    async submitOrder(pid: { paymentIntentId: string }) {
      const payload = {
        customer: this.customer,
        delivery_address: this.deliveryAddress,
        delivery_method: this.deliveryMethod,
        items: this.saleCartItems.map(
          ({
            product,
            quantity,
          }: {
            product: RetailProduct;
            quantity: number;
          }) => ({
            id: product.id,
            price: product.price,
            tax_rate: product.tax_rate,
            quantity,
          })
        ),
        payment_intent_id: pid.paymentIntentId,
        total: this.totalSalesIncludingDeliveryAndTax,
      };

      const result = await fetch("/api/checkout/complete", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN":
            document
              .querySelector("meta[name='csrf-token']")
              ?.getAttribute("content") ?? "",
        },
      });
      if (result.ok) {
        const data = await result.json();
        this.emptyCart(false, true);
        window.location.href = "/checkout-complete/" + data.sale_id;
      }
    },

    async submitCombinedOrder(pid: {
      paymentIntentId: string | null;
      customerId: number | null;
      userId: number | null;
    }) {
      if (this.hireSubmissionBlocked) {
        throw new Error("account_application_required");
      }

      const payload = {
        ...this.submitPayload,
        payment_intent_id: pid.paymentIntentId,
        customer_id: pid.customerId,
        user_id: pid.userId,
      };
      const result = await fetch("/api/v1/combined-checkout", {
        method: "POST",
        body: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN":
            document
              .querySelector("meta[name='csrf-token']")
              ?.getAttribute("content") ?? "",
        },
      });
      if (result.ok) {
        const data = await result.json();
        if (data.sale_id) {
          window.location.href = `/checkout-complete/${data.sale_id}?type=sale`;
        } else if (data.booking_id) {
          window.location.href = `/checkout-complete/${data.booking_id}?type=booking`;
        }
      }
      if (!result.ok) {
        throw new Error(
          "Unable to submit order, please contact us for assistance."
        );
      }
    },
  },

  persist: {
    storage: localStorage,
    key: `cart-v11`,
    afterHydrate: (context: PiniaPluginContext) => {
      context.store.items.forEach((item: BookingItem) => {
        if (item.on_hire) {
          item.on_hire = new Date(item.on_hire);
        }
        if (item.off_hire) {
          item.off_hire = new Date(item.off_hire);
        }
      });
      context.store.calculatingDeliveryPrice = false;
      pendingHireRecalcs.clear();
      context.store.accountApplicationSubmitted = false;
      context.store.requiresAccountApplicationForHire = false;
      context.store.deliveryPriceError = null;
    },
  },
});
